import { ICON_SIZE } from '@components/v2/constants';
import Loading from '@components/v2/ui/Loading/Loading';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import { forwardRef, useId } from 'react';
import { toExternalLinkProps, toLinkProps } from '../utils/toLinkProps';

const LOADING_INDICATOR_SIZE: Record<string, number> = {
  small: ICON_SIZE.xs,
  medium: ICON_SIZE.xs,
  large: ICON_SIZE.sm,
};

type BaseProps = Pick<
  MuiButtonProps,
  | 'size'
  | 'onClick'
  | 'disabled'
  | 'loading'
  | 'fullWidth'
  | 'children'
  | 'className'
  | 'sx'
  | 'startIcon'
  | 'endIcon'
  | 'type'
  | 'shape'
  | 'role'
  | 'tabIndex'
  | 'aria-selected'
  | 'aria-invalid'
  | 'aria-label'
  | 'aria-haspopup'
  | 'aria-expanded'
> & {
  size?: 'small' | 'medium' | 'large';
  testId?: string;
  isActive?: boolean;
  to?: string;
  href?: string;
};

type VariantProps =
  | { variant: 'contained'; color?: 'primary' | 'secondary' | 'error' }
  | { variant: 'outlined'; color?: 'primary' }
  | { variant?: 'text'; color?: 'primary' | 'secondary' | 'error' };

type Props = BaseProps & VariantProps;

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant,
      color,
      size,
      onClick,
      disabled,
      loading,
      fullWidth,
      children,
      className,
      sx,
      startIcon,
      endIcon,
      shape,
      type,
      testId,
      isActive,
      to,
      href,
      role,
      tabIndex,
      'aria-selected': ariaSelected,
      'aria-invalid': ariaInvalid,
      'aria-label': ariaLabel,
      'aria-haspopup': ariaHasPopup,
      'aria-expanded': ariaExpanded,
    },
    ref,
  ) => {
    const loadingId = useId();

    return (
      <MuiButton
        variant={variant}
        color={color}
        size={size}
        type={type}
        onClick={onClick}
        disabled={disabled}
        loading={loading}
        loadingPosition="end"
        fullWidth={fullWidth}
        className={className}
        sx={sx}
        id={loading ? loadingId : undefined}
        startIcon={startIcon}
        endIcon={endIcon}
        shape={shape}
        role={role}
        tabIndex={tabIndex}
        aria-selected={ariaSelected}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
        test-id={testId}
        data-active={isActive || undefined}
        ref={ref}
        loadingIndicator={
          <Loading
            size={LOADING_INDICATOR_SIZE[size ?? 'medium'] ?? ICON_SIZE.sm}
            color="inherit"
            aria-labelledby={loadingId}
          />
        }
        {...(toLinkProps(to) ?? toExternalLinkProps(href))}
      >
        {children}
      </MuiButton>
    );
  },
);

Button.displayName = 'Button';
export type { Props as ButtonProps };
export default Button;
